import { useSelectionStore } from '@/state/selection';
import { useCompareStore, type DiffKind, type DiffRow } from './state';

const KINDS: DiffKind[] = ['added', 'removed', 'changed'];
const MARK: Record<DiffKind, string> = { added: '+', removed: '-', changed: '~' };

export function ComparePanel() {
  const status = useCompareStore((s) => s.status);
  const diff = useCompareStore((s) => s.diff);
  const rows = useCompareStore((s) => s.rows);
  const swipe = useCompareStore((s) => s.swipe);
  const error = useCompareStore((s) => s.error);
  const setSwipe = useCompareStore((s) => s.setSwipe);
  const buildFirst = useCompareStore((s) => s.buildFirst);
  const download = useCompareStore((s) => s.download);
  const setFocus = useSelectionStore((s) => s.setFocus);
  const select = useSelectionStore((s) => s.select);

  const counts: Record<DiffKind, number> = {
    added: diff?.added.length ?? 0,
    removed: diff?.removed.length ?? 0,
    changed: diff?.changed.length ?? 0,
  };
  const focus = (row: DiffRow) => {
    if (!row.target) return;
    setFocus({ center: row.target.center, radius: row.target.radius });
    select({ layer: row.target.layer, expressID: row.target.expressID });
  };

  return (
    <div className="lens-panel compare">
      <div className="lens-bar">
        <span className="lens-dot" />
        <span>compare versions</span>
        <span className="lens-right">{status === 'diffing' ? 'diffing…' : 'by GlobalId'}</span>
      </div>
      {status === 'ready' ? (
        <>
          <dl className="lens-stats">
            {KINDS.map((k) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd data-kind={k}>{counts[k]}</dd>
              </div>
            ))}
          </dl>
          <label className="swipe">
            <span>before</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.001}
              value={swipe}
              onChange={(e) => setSwipe(Number(e.target.value))}
              aria-label="Wipe between before and after"
            />
            <span>after</span>
          </label>
          <ul className="diff-list">
            {rows.map((r) => (
              <li key={r.globalId}>
                <button type="button" disabled={!r.target} onClick={() => focus(r)}>
                  <span className="mark" data-kind={r.kind}>
                    {MARK[r.kind]}
                  </span>
                  <span className="row-name">
                    {r.type.replace(/^Ifc/i, '')} · {r.name}
                  </span>
                  <span className="row-meta">
                    {r.globalId}
                    {r.fields.length ? ` · ${r.fields.join(', ')}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="action" onClick={download}>
            Download diff (JSON)
          </button>
        </>
      ) : status === 'empty' ? (
        <>
          <p className="hint">Nothing to compare yet. Build something first.</p>
          <button type="button" className="action" onClick={buildFirst}>
            Put another floor on top
          </button>
        </>
      ) : (
        <p className="hint">{status === 'error' ? `diff failed: ${error}` : 'Comparing…'}</p>
      )}
      <p className="lens-foot">
        Left of the cut: the file as it came. Right of it: the model with your edits.
      </p>
    </div>
  );
}
